import {
  faCaretDown,
  faCaretRight,
  faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useClassroomContext } from 'contexts/ClassroomContext';
import { useGradebook } from 'contexts/TeacherGradebookContext';
import TeacherGradebookEntry from 'models/TeacherGradebookEntry';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, FilterOption } from 'view/components/common/Filter';
import UserInfoDropdownItem from '../classroom/UserInfoDropdownItem';
import DualColumnSelector from 'view/components/common/ItemSelector';

interface StudentSelectorProps {
  onUpdateSelectedStudents: (selectedStudents: TeacherGradebookEntry[]) => void;
}

type SelectableStudent = {
  student: TeacherGradebookEntry;
  selected: boolean;
  visible: boolean;
};

const StudentSelector: React.FC<StudentSelectorProps> = ({
  onUpdateSelectedStudents,
}) => {
  const [showStudentSelector, setShowStudentSelector] =
    useState<boolean>(false);
  const [students, setStudents] = useState<SelectableStudent[]>([]);
  const { classroom } = useClassroomContext();
  const { gradebook } = useGradebook();
  const [readingGroups, setReadingGroups] = useState<FilterOption[]>();

  useEffect(() => {
    // Set up the reading group options for this classroom
    if (
      !classroom ||
      classroom?.max_reading_level === classroom?.min_reading_level
    )
      return;

    const options: FilterOption[] = [];

    for (
      let level = classroom.min_reading_level;
      level <= classroom.max_reading_level;
      level++
    ) {
      options.push({
        is_active: true,
        value: level.toString(),
      });
    }

    setReadingGroups(options);
  }, [classroom]);

  useEffect(() => {
    if (!gradebook) return;
    setStudents(
      gradebook.students.map((s) => ({
        student: s,
        selected: true,
        visible: true,
      })),
    );
  }, [gradebook]);

  const handleReadingGroupToggled = (
    checked: boolean,
    option: FilterOption,
  ) => {
    if (option.value === 'All') {
      setReadingGroups((prev) =>
        prev?.map((rg) => ({ ...rg, is_active: true })),
      );
      return;
    }

    const updatedReadingGroups = readingGroups?.slice() ?? [];
    const optionIdx = readingGroups?.findIndex((o) => o.value === option.value);

    if (optionIdx === undefined || optionIdx === -1) return;

    updatedReadingGroups[optionIdx] = { ...option, is_active: checked };
    setReadingGroups(updatedReadingGroups);

    if (!gradebook) return [];

    const activeGroups = new Set(
      updatedReadingGroups.filter((g) => g.is_active).map((g) => g.value),
    );

    setStudents((prev: any) => {
      const updatedStudents = prev.map((s: SelectableStudent) => ({
        ...s,
        selected: activeGroups.has(s.student.reading_group.toString()),
        visible: activeGroups.has(s.student.reading_group.toString()),
      }));
      onUpdateSelectedStudents(
        updatedStudents
          .filter((s: any) => s.selected)
          .map((s: any) => s.student),
      );
      return updatedStudents;
    });
  };

  const activeReadingGroupSet = useMemo(() => {
    if (!readingGroups) return null;
    return new Set(
      readingGroups.filter((g) => g.is_active).map((g) => g.value),
    );
  }, [readingGroups]);

  const readingGroupFilter = useCallback(
    (item: SelectableStudent) => {
      if (!activeReadingGroupSet) return true;
      return Boolean(
        item.student.reading_group &&
          activeReadingGroupSet.has(item.student.reading_group.toString()),
      );
    },
    [activeReadingGroupSet],
  );

  const handleAddStudent = (student: SelectableStudent) => {
    setStudents((prev: any) => {
      const index = prev.findIndex((s: any) => s === student);
      if (index === -1) return;
      const updatedStudents = prev.slice();
      updatedStudents[index] = { ...student, selected: true };
      onUpdateSelectedStudents(
        updatedStudents
          .filter((s: any) => s.selected)
          .map((s: any) => s.student),
      );
      return updatedStudents;
    });
  };

  const handleRemoveStudent = (student: SelectableStudent) => {
    setStudents((prev: any) => {
      const index = prev.findIndex((s: any) => s === student);
      if (index === -1) return;
      const updatedStudents = prev.slice();
      updatedStudents[index] = { ...student, selected: false };
      onUpdateSelectedStudents(
        updatedStudents
          .filter((s: any) => s.selected)
          .map((s: any) => s.student),
      );
      return updatedStudents;
    });
  };

  return (
    <div className="assignment-attribute-container">
      <div className="sidebar-text">
        <FontAwesomeIcon icon={faClipboardCheck} />
        <label className="label-normal">Assign to</label>
      </div>
      {classroom?.min_reading_level !== classroom?.max_reading_level && (
        <div className="sidebar-text" style={{ justifyContent: 'left' }}>
          <Filter
            label="By reading group"
            type="multichoice"
            options={readingGroups}
            onOptionChange={handleReadingGroupToggled}
          />
        </div>
      )}
      <div
        className="row sidebar-text"
        onClick={() => setShowStudentSelector((prev) => !prev)}
      >
        <FontAwesomeIcon
          icon={showStudentSelector ? faCaretDown : faCaretRight}
        />
        <label className="label-small">Custom selection</label>
      </div>
      {showStudentSelector && (
        <DualColumnSelector
          items={students}
          selectedItems={students.filter((s) => s.selected)}
          style={{}}
          getId={(item: SelectableStudent) => item.student.username}
          renderItem={(item: SelectableStudent) => (
            <UserInfoDropdownItem
              user={item.student}
              classroom={classroom!}
              onSelect={() => {}}
            />
          )}
          filterItem={(items, searchTerm) =>
            items.filter((item) =>
              `${item.student.first_name} ${item.student.last_name} ${item.student.email}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase()),
            )
          }
          visibleFilter={readingGroupFilter}
          onAddItem={handleAddStudent}
          onRemoveItem={handleRemoveStudent}
        />
      )}
    </div>
  );
};

export default StudentSelector;
